<template>
  <div id="app" class="min-h-screen">
    <!-- Хедер только для авторизованных пользователей -->
    <header 
      v-if="isAuthenticated" 
      class="app-header p-3 border-b border-surface-200 dark:border-surface-700 bg-surface-0 dark:bg-surface-900"
    >
      <div class="flex justify-content-between align-items-center">
        <!-- Левая часть: кнопка меню -->
        <Button
          icon="pi pi-bars"
          severity="secondary"
          variant="text"
          @click="sidebarVisible = true"
          v-tooltip.right="'Меню'"
          class="menu-toggle-btn"
        />
        
        <!-- Правая часть: информация о пользователе и выход -->
        <div class="flex align-items-center gap-3">
          <div class="text-sm text-surface-700 dark:text-surface-300 text-right">
            <div class="font-medium">{{ userInfo.username }}</div>
            <div class="text-xs">{{ getRoleLabel(userInfo.role) }}</div>
          </div>
          <Button
            icon="pi pi-sign-out"
            severity="secondary"
            variant="text"
            @click="logout"
            v-tooltip.left="'Выйти'"
          />
        </div>
      </div>
    </header>

    <!-- Боковое меню -->
    <Sidebar 
      v-model:visible="sidebarVisible" 
      :showCloseIcon="true"
      class="app-sidebar"
    >
      <template #header>
        <div class="sidebar-header">
          <i class="pi pi-database text-primary text-2xl mr-2"></i>
          <span class="font-bold text-xl">AI База Данных Очков</span>
        </div>
      </template>
      
      <div class="sidebar-content">
        <nav class="sidebar-nav">
          <div 
            v-for="item in menuItems" 
            :key="item.value"
            class="nav-item"
            :class="{ 'active': selectedPage === item.value }"
            @click="navigateTo(item.value)"
          >
            <i :class="item.icon" class="nav-icon"></i>
            <span class="nav-label">{{ item.label }}</span>
          </div>
        </nav>
      </div>
      
      <template #footer>
        <div class="sidebar-footer text-sm text-surface-500">
          <div class="flex align-items-center gap-2">
            <i class="pi pi-user"></i>
            <span>{{ userInfo.username }}</span>
            <span class="text-xs">({{ getRoleLabel(userInfo.role) }})</span>
          </div>
        </div>
      </template>
    </Sidebar>

    <div class="app-content">
      <router-view />
    </div>

    <Toast />
    <ConfirmDialog />
  </div>
</template>

<script>
import { ref, computed, onMounted, watch } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import { useToast } from 'primevue/usetoast'
import Button from 'primevue/button'
import Sidebar from 'primevue/sidebar'
import Toast from 'primevue/toast'
import ConfirmDialog from 'primevue/confirmdialog'
import apiService from './services/apiService'

export default {
  name: 'App',
  components: {
    Button,
    Sidebar,
    Toast,
    ConfirmDialog
  },
  setup() {
    const router = useRouter()
    const route = useRoute()
    const toast = useToast()
    const selectedPage = ref('chat')
    const sidebarVisible = ref(false)

    // Информация о пользователе
    const userInfo = ref({
      username: '',
      role: 'support'
    })

    // Проверка авторизации
    const isAuthenticated = computed(() => {
      // eslint-disable-next-line no-unused-expressions
      route.fullPath
      return !!localStorage.getItem('access_token')
    })

    // Пункты меню (фильтруются по роли)
    const menuItems = computed(() => {
      const baseItems = [
        { label: 'Чат', value: 'chat', icon: 'pi pi-comments' },
        { label: 'Файлы', value: 'files', icon: 'pi pi-upload' },
        { label: 'Данные', value: 'data', icon: 'pi pi-table' },
        { label: 'Изображения', value: 'images', icon: 'pi pi-images' },
        { label: 'Учет оплат', value: 'payments', icon: 'pi pi-money-bill' },
        { label: 'Вознаграждения', value: 'rewards', icon: 'pi pi-gift' },
        { label: 'Синхронизация', value: 'sync', icon: 'pi pi-sync' },
        { label: 'Выгрузки', value: 'feeds', icon: 'pi pi-share-alt' },
        { label: 'Задачи', value: 'tasks', icon: 'pi pi-check-square' }
      ]
      
      // Добавляем админ-панель только для администраторов
      if (userInfo.value.role === 'admin') {
        baseItems.push({ label: 'Администрирование', value: 'admin', icon: 'pi pi-cog' })
      }

      // FAQ доступен всем, но визуально идёт после админ-панели (если она есть)
      baseItems.push({ label: 'Вопрос-Ответ', value: 'faq', icon: 'pi pi-question-circle' })
      
      return baseItems
    })

    // Получение информации о пользователе
    const loadUserInfo = () => {
      const savedUserInfo = localStorage.getItem('user_info')
      if (savedUserInfo) {
        userInfo.value = JSON.parse(savedUserInfo)
      }
    }

    // Получение роли на русском
    const getRoleLabel = (role) => {
      const roleLabels = {
        'admin': 'Администратор',
        'support': 'Поддержка'
      }
      return roleLabels[role] || role
    }

    // Выход из системы
    const logout = async () => {
      try {
        await apiService.logout()
      } catch (error) {
        console.error('Logout error:', error)
      } finally {
        localStorage.removeItem('access_token')
        localStorage.removeItem('user_info')
        
        toast.add({
          severity: 'info',
          summary: 'Выход',
          detail: 'Вы успешно вышли из системы',
          life: 3000
        })
        
        router.push('/login')
      }
    }

    // Навигация
    const navigateTo = (page) => {
      router.push(`/${page}`)
      sidebarVisible.value = false
    }

    // Отслеживание изменений роута
    watch(route, (newRoute) => {
      const path = newRoute.path
      const pages = ['chat', 'files', 'data', 'images', 'payments', 'rewards', 'sync', 'feeds', 'tasks', 'admin', 'faq']
      for (const page of pages) {
        if (path.includes(`/${page}`)) {
          selectedPage.value = page
          break
        }
      }
    })

    // Устанавливаем активную страницу при загрузке
    onMounted(() => {
      loadUserInfo()
      
      const currentPath = route.path
      const pages = ['files', 'data', 'images', 'payments', 'rewards', 'sync', 'feeds', 'tasks', 'admin', 'faq']
      selectedPage.value = 'chat'
      for (const page of pages) {
        if (currentPath.includes(`/${page}`)) {
          selectedPage.value = page
          break
        }
      }
    })

    // Подхватываем userInfo сразу после успешной авторизации
    watch(isAuthenticated, (authed) => {
      if (authed) {
        loadUserInfo()
      } else {
        userInfo.value = { username: '', role: 'support' }
      }
    })

    return {
      selectedPage,
      menuItems,
      navigateTo,
      isAuthenticated,
      userInfo,
      getRoleLabel,
      logout,
      sidebarVisible
    }
  }
}

</script>

<style>
#app {
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
  min-height: 100vh;
  background: var(--surface-ground);
}

body {
  margin: 0;
  padding: 0;
  background: var(--surface-ground);
}

.app-header {
  position: sticky;
  top: 0;
  z-index: 1000;
  backdrop-filter: blur(8px);
  background: rgba(var(--surface-0), 0.95);
}

.dark-mode .app-header {
  background: rgba(var(--surface-900), 0.95);
}

.app-content {
  min-height: calc(100vh - 60px);
}

/* Кнопка открытия меню */
.menu-toggle-btn {
  width: 40px;
  height: 40px;
}

.menu-toggle-btn:hover {
  background: var(--surface-100) !important;
}

/* Стили бокового меню */
.app-sidebar {
  width: 280px !important;
}

.app-sidebar .p-sidebar-header {
  padding: 1rem 1.25rem;
  border-bottom: 1px solid var(--surface-200);
}

.sidebar-header {
  display: flex;
  align-items: center;
}

.sidebar-content {
  padding: 1rem 0;
}

.sidebar-nav {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
}

.nav-item {
  display: flex;
  align-items: center;
  padding: 0.875rem 1.25rem;
  cursor: pointer;
  transition: all 0.2s ease;
  border-radius: 0;
  color: var(--text-color);
}

.nav-item:hover {
  background: var(--surface-100);
}

.nav-item.active {
  background: var(--primary-50);
  color: var(--primary-color);
  border-left: 3px solid var(--primary-color);
}

.nav-item.active .nav-icon {
  color: var(--primary-color);
}

.nav-icon {
  width: 20px;
  margin-right: 12px;
  font-size: 1.1rem;
  color: var(--text-color-secondary);
}

.nav-label {
  font-weight: 500;
  font-size: 0.95rem;
}

.sidebar-footer {
  padding: 1rem 1.25rem;
  border-top: 1px solid var(--surface-200);
}

/* Dark mode */
.dark-mode .nav-item:hover {
  background: var(--surface-800);
}

.dark-mode .nav-item.active {
  background: rgba(var(--primary-400), 0.15);
}

.dark-mode .sidebar-footer,
.dark-mode .app-sidebar .p-sidebar-header {
  border-color: var(--surface-700);
}

/* Мобильная адаптация */
@media (max-width: 768px) {
  .app-sidebar {
    width: 85vw !important;
    max-width: 300px !important;
  }
  .sidebar-footer .text-xs {
    display: none;
  }
}
</style>
